import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Maximize, ChevronLeft, CheckCircle, Clock, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const VideoPlayerPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  
  const [course, setCourse] = useState(null);
  const [videos, setVideos] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isTogglingCompletion, setIsTogglingCompletion] = useState(false);

  useEffect(() => {
    fetchCourseVideos();
  }, [courseId]);

  useEffect(() => {
    if (videos.length > 0) {
      loadVideo(currentVideoIndex);
    }
  }, [currentVideoIndex, videos]);

  const fetchCourseVideos = async () => {
    try {
      const response = await axios.get(`/videos/course/${courseId}`);
      setCourse(response.data.course);
      const videosData = response.data.videos || [];
      
      console.log('Vidéos chargées avec progression:', videosData);
      setVideos(videosData);
      
      // Rafraîchir les URLs toutes les heures (avant expiration)
      const refreshInterval = setInterval(() => {
        fetchCourseVideos();
      }, 3600000); // 1 heure
      
      if (videosData.length === 0) {
        toast.error('Aucune vidéo disponible pour ce cours');
      }
      
      return () => clearInterval(refreshInterval);
    } catch (error) {
      console.error('Erreur lors du chargement des vidéos:', error);
      
      if (error.response?.status === 403) {
        toast.error('Vous devez acheter ce cours pour accéder aux vidéos');
        navigate('/courses');
      } else {
        toast.error('Erreur lors du chargement des vidéos');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadVideo = (index) => {
    if (videoRef.current && videos[index]) {
      videoRef.current.src = videos[index].streamUrl;
      videoRef.current.load();
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted(!muted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
      
      // Sauvegarder la progression toutes les 5 secondes
      if (Math.floor(videoRef.current.currentTime) % 5 === 0) {
        saveProgress();
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = pos * videoRef.current.duration;
    }
  };

  const saveProgress = async () => {
    // Ne pas sauvegarder si on est en train de marquer manuellement
    if (!videos[currentVideoIndex] || !videoRef.current || isTogglingCompletion) return;
    
    const currentVideo = videos[currentVideoIndex];
    const isAlreadyCompleted = currentVideo.progress?.isCompleted || false;
    const shouldMarkCompleted = progress > 90;
    
    // Pour la sauvegarde automatique, garder le max pour ne pas perdre de progression
    const previousWatchedSeconds = currentVideo.progress?.watchedSeconds || 0;
    const currentWatchedSeconds = Math.floor(videoRef.current.currentTime);
    const watchedSeconds = Math.max(previousWatchedSeconds, currentWatchedSeconds);
    
    try {
      await axios.post(`/videos/${videos[currentVideoIndex].id}/progress`, {
        watchedSeconds: watchedSeconds,
        isCompleted: isAlreadyCompleted || shouldMarkCompleted
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la progression:', error);
    }
  };

  const toggleVideoCompletion = async () => {
    if (!videos[currentVideoIndex] || isTogglingCompletion) return;
    
    const currentVideo = videos[currentVideoIndex];
    const isCurrentlyCompleted = currentVideo.progress?.isCompleted || false;
    const newCompletionStatus = !isCurrentlyCompleted;
    
    // Si on marque comme terminée, vérifier la durée
    if (newCompletionStatus) {
      const videoDuration = currentVideo.duration || (videoRef.current?.duration ? Math.floor(videoRef.current.duration) : 0);
      
      if (videoDuration === 0) {
        toast.error('Impossible de marquer la vidéo : durée inconnue');
        return;
      }
    }
    
    setIsTogglingCompletion(true);
    
    try {
      const watchedSeconds = newCompletionStatus 
        ? (currentVideo.duration || Math.floor(videoRef.current?.duration || 0))
        : 0;
      
      // Appel API
      await axios.post(`/videos/${currentVideo.id}/progress`, {
        watchedSeconds: watchedSeconds,
        isCompleted: newCompletionStatus
      });
      
      // Mettre à jour l'état local une seule fois
      setVideos(prevVideos => {
        const updatedVideos = [...prevVideos];
        updatedVideos[currentVideoIndex] = {
          ...updatedVideos[currentVideoIndex],
          progress: {
            watchedSeconds: watchedSeconds,
            isCompleted: newCompletionStatus
          }
        };
        return updatedVideos;
      });
      
      if (newCompletionStatus) {
        toast.success('Vidéo marquée comme terminée !');
        // Passer à la vidéo suivante après un court délai
        setTimeout(() => {
          if (currentVideoIndex < videos.length - 1) {
            goToNextVideo();
          }
        }, 1000);
      } else {
        toast.success('Vidéo marquée comme non terminée');
      }
      
    } catch (error) {
      console.error('Erreur lors du marquage de la vidéo:', error);
      toast.error('Erreur lors du marquage de la vidéo');
    } finally {
      setIsTogglingCompletion(false);
    }
  };

  const goToNextVideo = () => {
    if (currentVideoIndex < videos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
      setPlaying(false);
    } else {
      toast.success('Vous avez terminé toutes les vidéos du cours !');
    }
  };

  const goToPreviousVideo = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(currentVideoIndex - 1);
      setPlaying(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Aucune vidéo disponible</h2>
          <button onClick={() => navigate('/dashboard')} className="btn btn-primary">
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentVideo = videos[currentVideoIndex];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-white hover:text-primary-400 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Retour à mes cours
            </button>
            <h1 className="text-lg font-semibold text-white">{course?.title}</h1>
            <div className="w-32"></div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lecteur vidéo */}
          <div className="lg:col-span-2">
            <div className="bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                className="w-full aspect-video"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={goToNextVideo}
                onClick={togglePlay}
              >
                Votre navigateur ne supporte pas la lecture vidéo.
              </video>

              {/* Contrôles */}
              <div className="bg-gray-800 p-4">
                {/* Barre de progression */}
                <div 
                  className="w-full h-2 bg-gray-600 rounded-full cursor-pointer mb-4"
                  onClick={handleSeek}
                >
                  <div 
                    className="h-full bg-primary-500 rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button onClick={togglePlay} className="text-white hover:text-primary-400">
                      {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                    </button>
                    <button onClick={goToPreviousVideo} disabled={currentVideoIndex === 0} className="text-white hover:text-primary-400 disabled:opacity-50">
                      <SkipBack className="w-5 h-5" />
                    </button>
                    <button onClick={goToNextVideo} disabled={currentVideoIndex === videos.length - 1} className="text-white hover:text-primary-400 disabled:opacity-50">
                      <SkipForward className="w-5 h-5" />
                    </button>
                    <button onClick={toggleMute} className="text-white hover:text-primary-400">
                      {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>
                    <span className="text-white text-sm">
                      {formatTime(videoRef.current?.currentTime)} / {formatTime(duration)}
                    </span>
                  </div>
                  <button onClick={toggleFullscreen} className="text-white hover:text-primary-400">
                    <Maximize className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Info vidéo */}
            <div className="mt-6 bg-gray-800 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-white mb-2">{currentVideo.title}</h2>
                  {currentVideo.description && (
                    <p className="text-gray-300">{currentVideo.description}</p>
                  )}
                </div>
                <button
                  onClick={toggleVideoCompletion}
                  disabled={isTogglingCompletion}
                  className={`ml-4 flex items-center px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                    isTogglingCompletion 
                      ? 'bg-gray-500 cursor-not-allowed text-white'
                      : currentVideo.progress?.isCompleted
                        ? 'bg-orange-600 hover:bg-orange-700 text-white'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {isTogglingCompletion ? (
                    <>
                      <div className="w-5 h-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Mise à jour...
                    </>
                  ) : currentVideo.progress?.isCompleted ? (
                    <>
                      <XCircle className="w-5 h-5 mr-2" />
                      Marquer comme non lu
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Marquer comme lu
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Liste des vidéos */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">
                Contenu du cours ({videos.length} vidéos)
              </h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {videos.map((video, index) => (
                  <button
                    key={video.id}
                    onClick={() => setCurrentVideoIndex(index)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      index === currentVideoIndex
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center mr-3">
                        {video.progress?.isCompleted ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : (
                          <span className="text-sm font-medium">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{video.title}</p>
                        <div className="flex items-center text-sm mt-1">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatTime(video.duration)}
                        </div>
                        {video.progress && video.progress.watchedSeconds > 0 && !video.progress.isCompleted && (
                          <div className="mt-2 w-full h-1 bg-gray-600 rounded-full">
                            <div 
                              className="h-full bg-primary-400 rounded-full"
                              style={{ width: `${(video.progress.watchedSeconds / video.duration) * 100}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerPage;
